import React, { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { Loader } from "../Loader";

interface InfiniteListProps {
    className?: string;
    isLoading: boolean;
    hasMore?: boolean;
    onLoadMore: () => void;
    children: ReactNode;
}

/**
 * Component for implementing infinite scroll functionality.
 * It shows a loader whenever the next page of items is loading.
 * It calls `onLoadMore` whenever the very bottom of the list is visible on the screen.
 * That allows it to work both "windowed" (with fixed height) and when the list is changing the height of the whole page.
 * Unlike `Virtuoso` it doesn't require a fixed height.
 */
export const InfiniteList = ({ className, isLoading, hasMore = false, onLoadMore, children }: InfiniteListProps) => {
    const lastElementRef = useRef<HTMLDivElement>(null);
    const isLastElementOnScreen = useOnScreen(lastElementRef);

    useEffect(() => {
        if (hasMore && isLastElementOnScreen && !isLoading) {
            onLoadMore();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLastElementOnScreen]);

    return (
        <div className={className}>
            {children}
            <div ref={lastElementRef} />
            {isLoading ? <Loader /> : null}
        </div>
    );
};

const useOnScreen = (ref: RefObject<HTMLElement | null>) => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [isOnScreen, setIsOnScreen] = useState(false);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(([entry]) => {
            setIsOnScreen(entry.isIntersecting);
        });
    }, []);

    useEffect(() => {
        observerRef.current?.observe(ref.current!);

        return () => {
            observerRef.current?.disconnect();
        };
    }, [ref]);

    return isOnScreen;
};
